import _uniq from 'lodash/uniq';

/**
 * Structural shape of the API's `AuthorizationResult` (`canDestroy`,
 * `canUpdate`, `canArchive`, …). Typed structurally so the helpers work with
 * every app's generated `graphql-common` types.
 */
export type AuthorizationResultLike = {
  value?: boolean | null;
  reasons?: { fullMessages?: string[] | null } | null;
} | null;

/**
 * The localised, ready-to-display explanation(s) behind a refused `can*` field.
 *
 * Note the sibling `message` field is NOT the reason — it is always the generic
 * "You are not authorized to perform this action.", whatever the actual cause.
 * The specific text only ever lives under `reasons.fullMessages`.
 */
export function getAuthorizationReasons(
  result?: AuthorizationResultLike,
): string[] {
  const fullMessages = result?.reasons?.fullMessages || [];
  return _uniq(fullMessages.filter(Boolean));
}

/**
 * The same explanation as a single string, ready for a tooltip body. Returns an
 * empty string when the API gave no reason, so callers can fall back to their
 * own copy.
 */
export function getAuthorizationReason(
  result?: AuthorizationResultLike,
): string {
  return getAuthorizationReasons(result).join(' ');
}

/**
 * Reasons across several records, de-duplicated — a bulk action that was
 * blocked on N records usually trips the same rule on most of them.
 */
export function getAuthorizationReasonsFor(
  results: Array<AuthorizationResultLike | undefined>,
): string[] {
  return _uniq(results.flatMap((result) => getAuthorizationReasons(result)));
}
